import { MutableRefObject, useMemo } from 'react'

import { IconPaperclip } from '@tabler/icons-react'
import { Form } from 'antd'

import { BlockBase, Attachment } from '../types/blueprint'

import { CaptureBlockActionButton } from './CaptureBlock.style'
import { getAttachmentsId } from './utils/mapValuesToTemplate'

import { Uploader } from '@/components/shared-components/BUI/Uploader'
import { ACCEPT } from '@/config/constants/fileTypes'
import { usePreventSaving } from '@/context/preventSavingContext'
import { useTemplateRenderer } from '@/context/templateRendererContext'
import { FileUploadResult } from '@/hooks/useAttachmentUpload'

import { AttachmentType, Resource } from 'types/api.graphql'

type BetterInnerProps = {
  value?: Attachment[]
  onChange?: (attachments: Attachment[]) => void
  renderAttachmentsElementId?: string
}

const BetterFieldAttachmentUploader: React.FC<BetterInnerProps> = ({
  value = [],
  onChange,
  renderAttachmentsElementId,
}) => {
  const { isSavingBlocked } = usePreventSaving()

  const {
    projectId,
    resource,
    config: { contextualDate },
  } = useTemplateRenderer()

  const fileList = useMemo(() => {
    return value.map<FileUploadResult>((attachment) => ({
      attachmentId: attachment.id,
      uid: attachment.id,
      name: attachment.name,
      url: attachment.url,
      percent: attachment.percent,
      thumbUrl: attachment.thumbnailUrl,
    }))
  }, [value])

  const onUploaderChange = (files: FileUploadResult[]) => {
    onChange(
      files.map<Attachment>((file) => ({
        id: file.attachmentId ?? file.uid,
        name: file.name,
        url: file.url,
        thumbnailUrl: file.thumbUrl ?? file.url,
        percent: file.percent,
      }))
    )
  }

  return (
    <Uploader
      attachmentResource={resource ?? Resource.TemplateResponse}
      attachmentType={AttachmentType.TemplateResponseMedia}
      projectId={projectId}
      attachmentSortDate={contextualDate}
      renderAttachmentsElementId={renderAttachmentsElementId}
      customUploader={(_a, _b, fileInput) => (
        <CaptureBlockActionButton
          icon={<IconPaperclip size={12} />}
          onClick={() => fileInput?.click()} // buttons inside labels don't work, need to manually trigger file input
          disabled={isSavingBlocked}
        >
          {isSavingBlocked ? 'Uploading...' : 'Attachment'}
        </CaptureBlockActionButton>
      )}
      fileList={fileList}
      onChange={onUploaderChange}
      accept={ACCEPT}
    />
  )
}

type Props = {
  container: MutableRefObject<HTMLElement>
  parentId: string
  attachments: BlockBase['attachments']
  renderAttachmentsElementId?: string
}

export const CaptureAttachments: React.FC<Props> = ({
  parentId,
  attachments,
  renderAttachmentsElementId,
}) => {
  if (attachments === undefined || attachments === false) return null

  const attachmentsId = getAttachmentsId(parentId)
  const initialValue = Array.isArray(attachments) ? attachments : []

  return (
    <Form.Item name={attachmentsId} initialValue={initialValue} noStyle>
      <BetterFieldAttachmentUploader
        renderAttachmentsElementId={renderAttachmentsElementId}
      />
    </Form.Item>
  )
}
